import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { CheckCircle2, AlertCircle, PackageCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * ShipmentLineChecklist — auto-populates the shipment dialog with the picked
 * order's produced items (stock >= requested quantity). Each row exposes a
 * "sevk edilecek adet" input (default: kalan adet, üst limit kalan) — the
 * user confirms per line and the aggregated summary (Ürün Özeti + Beden
 * Özeti + items[{line_id, qty}]) is pushed back to the parent form.
 */
export default function ShipmentLineChecklist({ orderId, onSummaryChange }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState({}); // line_id → bool
  const [qtys, setQtys] = useState({}); // line_id → number

  const remainingOf = (ln) =>
    ln.remaining_qty ?? Math.max(0, Number(ln.quantity || 0) - Number(ln.shipped_qty || 0));

  useEffect(() => {
    if (!orderId) { setLines([]); setChecked({}); setQtys({}); return; }
    setLoading(true);
    api.get(`/shipments/orders/${orderId}/producible`)
      .then(({ data }) => {
        setLines(data || []);
        // Auto-tick lines that still have remaining qty to ship
        const initChecked = {};
        const initQty = {};
        (data || []).forEach((ln) => {
          const rem = ln.remaining_qty ?? Math.max(0, Number(ln.quantity || 0) - Number(ln.shipped_qty || 0));
          initQty[ln.id] = rem;
          if (rem > 0) initChecked[ln.id] = true;
        });
        setChecked(initChecked);
        setQtys(initQty);
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [orderId]);

  const selectedLines = useMemo(
    () => lines.filter((ln) => checked[ln.id] && Number(qtys[ln.id] || 0) > 0),
    [lines, checked, qtys],
  );

  // Push aggregated summary up to parent form
  useEffect(() => {
    if (!onSummaryChange) return;
    const productSummary = selectedLines
      .map((ln) => `${ln.product_name}${ln.variant_sku ? ` (${ln.variant_sku})` : ""} × ${Number(qtys[ln.id] || 0)}`)
      .join(" · ");
    const sizeAgg = {};
    selectedLines.forEach((ln) => {
      Object.entries(ln.size_breakdown || {}).forEach(([k, v]) => {
        sizeAgg[k] = (sizeAgg[k] || 0) + Number(v || 0);
      });
    });
    const sizeSummary = Object.entries(sizeAgg)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `${k}:${v}`)
      .join(", ");
    onSummaryChange({
      lineCount: selectedLines.length,
      totalQty: selectedLines.reduce((s, ln) => s + Number(qtys[ln.id] || 0), 0),
      productSummary,
      sizeSummary,
      lineIds: selectedLines.map((ln) => ln.id),
      items: selectedLines.map((ln) => ({ line_id: ln.id, qty: Number(qtys[ln.id] || 0) })),
    });
  }, [selectedLines, qtys, onSummaryChange]);

  const toggle = (id) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  const setQty = (ln, raw) => {
    const max = remainingOf(ln);
    let v = parseInt(raw, 10);
    if (Number.isNaN(v)) v = 0;
    if (v > max) v = max;
    if (v < 0) v = 0;
    setQtys((prev) => ({ ...prev, [ln.id]: v }));
  };

  if (!orderId) return null;

  return (
    <div className="border border-slate-800 rounded-sm bg-slate-900/40 p-3 space-y-2" data-testid="shipment-line-checklist">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase text-slate-500 font-mono flex items-center gap-1.5">
          <PackageCheck className="w-3.5 h-3.5 text-emerald-400" /> Sevk Edilecek Ürünler
        </Label>
        <span className="text-[10px] font-mono text-slate-500">
          {selectedLines.length} / {lines.length} satır seçili
        </span>
      </div>

      {loading && (
        <div className="text-xs text-slate-400 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" /> Üretilen ürünler yükleniyor…
        </div>
      )}

      {!loading && lines.length === 0 && (
        <div className="text-xs text-slate-500 font-mono">Bu siparişte tanımlı satır bulunamadı.</div>
      )}

      {!loading && lines.length > 0 && lines.every((ln) => remainingOf(ln) <= 0) && (
        <div className="text-xs text-emerald-400 font-mono" data-testid="checklist-all-shipped">
          Bu siparişin tüm kalemleri sevk edildi.
        </div>
      )}

      <div className="divide-y divide-slate-800/60">
        {lines.map((ln) => {
          const matches = ln.matches_count;
          const remaining = remainingOf(ln);
          const shipped = Number(ln.shipped_qty || 0);
          const sizes = Object.entries(ln.size_breakdown || {})
            .filter(([, v]) => v > 0)
            .map(([k, v]) => `${k}:${v}`)
            .join(", ");
          return (
            <div key={ln.id} className="flex items-start gap-2 py-2" data-testid={`checklist-row-${ln.id}`}>
              <Checkbox
                data-testid={`checklist-tick-${ln.id}`}
                checked={!!checked[ln.id]}
                onCheckedChange={() => toggle(ln.id)}
                disabled={remaining <= 0}
                className="mt-1 border-slate-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-100">{ln.product_name}</span>
                  {ln.variant_sku && <span className="text-[10px] font-mono text-slate-500">{ln.variant_sku}</span>}
                </div>
                {(() => {
                  const attrs = ln.variant_attributes || {};
                  const beden = attrs.beden || attrs.Beden || attrs.size;
                  const renk = attrs.renk || attrs.Renk || attrs.color;
                  const bits = [];
                  if (beden) bits.push(`Beden: ${beden}`);
                  if (renk) bits.push(`Renk: ${renk}`);
                  return bits.length ? (
                    <div className="text-[10px] text-slate-300 font-mono mt-0.5">{bits.join(" · ")}</div>
                  ) : null;
                })()}
                {sizes && (
                  <div className="text-[10px] text-slate-400 font-mono mt-0.5">Beden Dağılımı: {sizes}</div>
                )}
                <div className="flex items-center gap-3 mt-1 text-[10px] font-mono flex-wrap">
                  <span className="text-slate-500">İstenen: <span className="text-slate-200 font-semibold">{ln.quantity}</span></span>
                  <span className="text-slate-500">Sevk Edilen: <span className="text-slate-200 font-semibold" data-testid={`checklist-shipped-${ln.id}`}>{shipped}</span></span>
                  <span className="text-slate-500">Kalan: <span className={remaining > 0 ? "text-amber-300 font-semibold" : "text-emerald-400 font-semibold"} data-testid={`checklist-remaining-${ln.id}`}>{remaining}</span></span>
                  <span className="text-slate-500">Üretilen/Stok: <span className={matches ? "text-emerald-400 font-semibold" : "text-yellow-400 font-semibold"}>{ln.produced_qty}</span></span>
                  {remaining <= 0 ? (
                    <span className="inline-flex items-center gap-1 text-emerald-400">
                      <CheckCircle2 className="w-3 h-3" /> Tamamlandı
                    </span>
                  ) : matches ? (
                    <span className="inline-flex items-center gap-1 text-emerald-400">
                      <CheckCircle2 className="w-3 h-3" /> Adet doğru
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-yellow-400">
                      <AlertCircle className="w-3 h-3" /> Eksik
                    </span>
                  )}
                </div>
                {checked[ln.id] && remaining > 0 && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <Label className="text-[10px] uppercase text-slate-500 font-mono shrink-0">Sevk Adedi</Label>
                    <Input
                      data-testid={`checklist-qty-${ln.id}`}
                      type="number"
                      min={0}
                      max={remaining}
                      value={qtys[ln.id] ?? remaining}
                      onChange={(e) => setQty(ln, e.target.value)}
                      className="bg-slate-950 border-slate-700 rounded-sm h-7 w-24 text-xs font-mono"
                    />
                    <span className="text-[10px] text-slate-500 font-mono">/ {remaining}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
